import { useEffect, useRef, useState } from 'react';
import { Link } from '../app/router';
import { useLaunchInfo } from '../data/launch';

/**
 * The token's contract address and where to trade it: a slim bar across the top of the Play screen (the landing page).
 * Read at runtime from /launch.json, so it is filled in right after the deployment without rebuilding the site
 * (deploy/cloudflare/make-launch-json.mjs). Before launch it says so and warns that any address posted elsewhere is not
 * RUNNER's.
 */
const short = (a: string) => `${a.slice(0, 6)}…${a.slice(-4)}`;

export function LaunchBar({ standalone = false }: { standalone?: boolean }) {
  const info = useLaunchInfo();
  const [copyStatus, setCopyStatus] = useState('Copy');
  const menu = useRef<HTMLDetailsElement>(null);
  // A <details> menu stays open until toggled; close it on an outside click or Escape like any dropdown.
  useEffect(() => {
    const close = (e: Event) => {
      const m = menu.current;
      if (!m?.open) return;
      if (e instanceof KeyboardEvent ? e.key === 'Escape' : !m.contains(e.target as Node)) m.open = false;
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', close);
    };
  }, []);
  if (!info) return null;
  const x = info.x ? (
    <a href={info.x} target="_blank" rel="noopener noreferrer">
      @Runnerdotparty
    </a>
  ) : (
    'our X account'
  );

  if (info.status !== 'live' || !info.token) {
    return (
      <div className="launch-bar" role="region" aria-label="Launch" data-testid="launch-card">
        <strong>Launching soon on {info.chainName}.</strong>
        <span className="launch-note">The contract address will appear here and on {x}. Any address posted elsewhere before that is not RUNNER.</span>
      </div>
    );
  }

  const ca = info.token;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ca);
      setCopyStatus('Copied');
      setTimeout(() => setCopyStatus('Copy'), 1500);
    } catch { setCopyStatus('Try again'); }
  };
  const chart = info.links.find(l => l.label === 'DexScreener');
  const community = info.links.find(l => l.label === 'Telegram');
  const explorer = info.links.find(l => l.label === 'Blockscout') ?? info.links.find(l => l.label === 'Etherscan');
  // Always visible on arrival: official CA, chart, community, and all remaining destinations in Links.
  return (
    <div className="launch-bar live" role="region" aria-label="Token contract" data-testid="launch-card">
      <button type="button" className="launch-ca" onClick={copy} title={`RUNNER on ${info.chainName} · Copy ${ca}`} aria-label={`Copy RUNNER contract address ${ca}`} data-testid="launch-ca">
        <span className="launch-ca-label">CA</span>
        <code className="mono">
          <span className="ca-full">{ca}</span>
          <span className="ca-short">{short(ca)}</span>
        </code>
        <span className="launch-ca-copy" aria-live="polite">
          {copyStatus}
        </span>
      </button>
      <nav className="launch-quick-links" aria-label="Official RUNNER links">
        {chart && <a href={chart.url} target="_blank" rel="noopener noreferrer">Chart <span aria-hidden="true">↗</span></a>}
        {community && <a className="launch-community" href={community.url} target="_blank" rel="noopener noreferrer">Telegram <span aria-hidden="true">↗</span></a>}
        {info.x && <a href={info.x} target="_blank" rel="noopener noreferrer" aria-label="RUNNER on X">X <span aria-hidden="true">↗</span></a>}
        {explorer && <a href={explorer.url} target="_blank" rel="noopener noreferrer">Explorer <span aria-hidden="true">↗</span></a>}
      </nav>
      <details ref={menu} className="launch-menu">
        <summary className="btn small ghost" aria-label="More official links">•••</summary>
        <div className="launch-menu-panel">
          <p className="launch-note">RUNNER contract<br /><code className="mono">{ca}</code></p>
          {standalone ? <a href="/trade">Trade on RUNNER</a> : <Link to="/trade">Trade on RUNNER</Link>}
          {info.links.map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer">
              {l.label}
            </a>
          ))}
          {info.x ? (
            <a href={info.x} target="_blank" rel="noopener noreferrer">
              X @Runnerdotparty
            </a>
          ) : null}
          <p className="launch-note">
            {info.quote ? `${info.quote} pair on Uniswap v4, ${info.chainName}. ` : ''}Check the address here and on {x}: copycats use the same name.
          </p>
        </div>
      </details>
    </div>
  );
}
